/* eslint-disable no-unused-expressions */
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useConnector, useInstantSearch } from 'react-instantsearch-hooks-web';
import { classnames } from 'tailwindcss-classnames';
import SearchSort from './SearchSort';
import 'react-datepicker/dist/react-datepicker.css';
import TypeFacet from './Facets/TypeFacet';
import SvgIcon from 'components/helpers/SvgIcon/SvgIcon';
import DepartmentFacet from './Facets/DepartmentFacet';
import ClassificationFacet from './Facets/ClassificationFacet';
import AuthorFacet from './Facets/AuthorFacet';
import DateRangeFacet from './Facets/DateRangeFacet';
import EventCategoryFacet from './Facets/EventCategoryFacet';
import ClearFacets from './Facets/ClearFacets';
import { useSearchDictionary } from './util';
import FileTypeFacet from './Facets/FileTypeFacet';
import ManagerFacet from './Facets/ManagerFacet';
import { SearchWrapperProps } from './SearchTypes';
import { useMenuOpen } from 'lib/util/toggle-menu';
import Button from '../Button';
import type {
  StatsConnectorParams,
  StatsWidgetDescription,
} from 'instantsearch.js/es/connectors/stats/connectStats';
import connectStats from 'instantsearch.js/es/connectors/stats/connectStats';
import EmployeeBlogAuthorFacet from './Facets/NmdpConnect/EmployeeBlog/EmployeeBlogAuthorFacet';
import EmployeeBlogTopicsFacet from './Facets/NmdpConnect/EmployeeBlog/EmployeeBlogTopicsFacet';
import EmployeeBlogYearFacet from './Facets/NmdpConnect/EmployeeBlog/EmployeeBlogYearFacet';
import { isEmployeeBlog } from 'lib/util/employee-blog-utils';

interface IMyProps {
  filters: string[];
}

function useStats(props?: StatsConnectorParams) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useConnector<StatsConnectorParams, StatsWidgetDescription>(connectStats as any, props);
}

const SearchFacetsWrapper = ({ children }: SearchWrapperProps) => {
  return <div className={classnames('mb-4')}>{children}</div>;
};

const SearchFacetsWithSearch = ({ filters }: IMyProps) => {
  const { nbHits } = useStats();
  const { indexUiState } = useInstantSearch();
  const selectedType = indexUiState.menu?.type;
  const dict = useSearchDictionary();
  const resultsCount = dict.resultsCountNoQuery.replace('@count', nbHits.toString());
  const { toggleMenu, isMenuOpen } = useMenuOpen();
  const employeeBlog = filters.includes('employeeBlog');
  const menuClasses = classnames(
    'bg-black',
    'bg-opacity-30',
    'duration-300',
    'fixed',
    'h-full',
    'w-full',
    'top-0',
    'transition-transform',
    'transform',
    'left-[100%]',
    'lg:relative',
    'lg:bg-theme-bg',
    {
      '-translate-x-full': isMenuOpen,
      'translate-x-0': !isMenuOpen,
      'lg:left-0': !isMenuOpen,
    }
  );
  return (
    <SearchFacetsWrapper>
      <div className={classnames('block', 'lg:hidden')}>
        <Button
          variant="secondary"
          className={classnames('mb-6', 'w-full', 'z-20')}
          onClick={toggleMenu}
        >
          Filter & Sort
        </Button>
      </div>
      <div className={menuClasses}>
        <div
          className={classnames(
            'absolute',
            'border',
            'border-gray-midGray',
            'box-border',
            'overflow-scroll',
            'h-full',
            'min-w-[200px]',
            'max-w-2xl',
            'py-6',
            'px-4',
            'w-fit',
            'w-full',
            'top-0',
            'right-0',
            'bg-white',
            'lg:relative',
            'lg:rounded-xl',
            'lg:min-w-full',
            'lg:overflow-hidden',
            'z-20'
          )}
        >
          <div className={classnames('flex', 'flex-col', 'lg:hidden')}>
            <Button
              variant="link"
              className={classnames('mb-6', 'z-20', 'text-right')}
              onClick={toggleMenu}
            >
              <span className={classnames('text-black')}>Close filters ✕</span>
            </Button>
            <Button
              variant="primary"
              className={classnames('mb-6', 'z-20', 'block', 'lg:hidden')}
              onClick={toggleMenu}
            >
              {resultsCount}
            </Button>
          </div>
          <hr className={classnames('mb-4', 'border-gray-midGray', 'lg:hidden')} />
          <div className={classnames('mb-6', 'flex', 'w-full', 'justify-between', 'items-center')}>
            <h3 className={classnames('text-blue-navy', 'heading-md')}>
              <SvgIcon
                icon="filter"
                size="xs"
                viewBoxSize="0 0 24 24"
                className={classnames('mr-2', '-mt-1')}
              />
              {dict.filter}
            </h3>
            <ClearFacets />
          </div>
          <div className={classnames('block', 'lg:hidden')}>
            <SearchSort />
          </div>
          {employeeBlog ? (
            <div className={classnames('w-full', 'justify-between')}>
              <EmployeeBlogAuthorFacet />
              <hr className={classnames('mb-4', 'border-gray-midGray')} />
              <EmployeeBlogTopicsFacet />
              <hr className={classnames('mb-4', 'border-gray-midGray')} />
              <EmployeeBlogYearFacet />
            </div>
          ) : (
            <TypeFacet filters={filters} />
          )}

          <div className={classnames({ hidden: selectedType !== 'employee' })}>
            <DepartmentFacet />
            <hr className={classnames('my-6')} />
            <ManagerFacet />
            <hr className={classnames('my-6')} />
            <ClassificationFacet />
          </div>

          <div className={classnames({ hidden: selectedType !== 'article' })}>
            <AuthorFacet />
            <hr className={classnames('my-6')} />
            <DateRangeFacet
              label={dict.articleDate}
              dateField="publishedDate"
              startFieldName="articleStartDate"
              endFieldName="articleEndDate"
              disallowDateRange={false}
            />
          </div>

          <div className={classnames({ hidden: selectedType !== 'event' })}>
            <EventCategoryFacet />
            <hr className={classnames('my-6')} />
            <DateRangeFacet
              label={dict.eventDate}
              dateField="startDate"
              startFieldName="eventStartDate"
              endFieldName="eventEndDate"
              disallowDateRange
            />
          </div>

          <div className={classnames({ hidden: selectedType !== 'file' })}>
            <FileTypeFacet />
          </div>
        </div>
      </div>
    </SearchFacetsWrapper>
  );
};

const SearchFacets = ({ filters }: IMyProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const pageEditing = sitecoreContext.pageEditing === true;
  const employeeBlog = isEmployeeBlog(sitecoreContext);
  // Set filters based on employee blog check
  const finalFilters = employeeBlog ? ['employeeBlog'] : filters;

  if (pageEditing) {
    return (
      <SearchFacetsWrapper>
        <div className={classnames('mb-6', 'flex', 'w-full', 'justify-between', 'items-center')}>
          <h3 className={classnames('text-blue-navy', 'heading-md')}>
            <SvgIcon
              icon="filter"
              size="xs"
              viewBoxSize="0 0 24 24"
              className={classnames('mr-2', '-mt-1')}
            />
            Search Facets
          </h3>
        </div>
      </SearchFacetsWrapper>
    );
  }
  return <SearchFacetsWithSearch filters={finalFilters} />;
};

export default SearchFacets;
